import { useEffect } from 'react';
import type { UseSortByProps } from 'react-instantsearch-core';
import { useSortBy } from 'react-instantsearch-core';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { Grid } from '@/components/Grid';
import { Radio } from '@/components/Radio';
import { Typography } from '@/components/Typography';
import { useListingSortBy } from '@/hooks/useListingSortBy';
import { algoliaStateActions } from '@/states/algoliaState';

export const SortByFilterWrapper = (
  props: Omit<UseSortByProps, 'items'>,
): JSX.Element => {
  const items: Array<{ label: string; value: string }> = useListingSortBy();
  const { currentRefinement, options, refine } = useSortBy({
    ...props,
    items,
  });
  useEffect(() => {
    algoliaStateActions.updateSortBy(currentRefinement);
  }, []);
  const handleRefine = useMemoizedFn((value: string) => {
    algoliaStateActions.updateSortBy(value);
    refine(value);
  });

  return (
    <SortByFilter
      currentRefinement={currentRefinement}
      data-test-id='SortByFilterWrapper'
      options={options}
      refine={handleRefine}
    />
  );
};

export type SortByFilterProps = {
  currentRefinement: string;
  options: Array<{ label: string; value: string }>;
  refine: (value: string) => void;
};

export const SortByFilter: React.FC<SortByFilterProps> = ({
  currentRefinement,
  options,
  refine,
}) => {
  return (
    <Grid>
      {options.map(({ label, value }, index) => (
        <Radio
          key={value}
          checked={value === currentRefinement}
          data-test-id={`SortByFilterRadio-${index}`}
          label={
            <Typography
              data-test-id={`SortByFilterTypography-${index}`}
              textTransform='capitalize'
            >
              {label}
            </Typography>
          }
          name='SortByFilter'
          onChange={() => refine(value)}
        />
      ))}
    </Grid>
  );
};

SortByFilter.displayName = 'SortByFilter';
